/**
 * Decodes attachment base64 into a Blob object URL once and caches it (bounded LRU).
 *
 * Received/sent attachments arrive as base64 embedded in the message payload and are
 * rendered as `data:` URIs. The browser re-decodes a `data:` URI every time its <img>
 * mounts — e.g. on every conversation switch — which shows up in DevTools as a repeated
 * "transfer" of the base64. Handing the <img> a stable `blob:` URL instead lets the
 * browser reuse the already-decoded image across remounts, so the decode happens once.
 *
 * Keys are message identities (`{direction-prefix}:{msgId}`), but msgIds are not a safe
 * identity for optimistic sends (the pre-ack id is a per-conversation guess that can
 * collide with another conversation's real smsgid), so each entry also carries a cheap
 * content fingerprint — a key hit with a different fingerprint is a collision and the
 * entry is replaced rather than served.
 *
 * URLs are never revoked while the session runs: a revoked URL breaks any still-mounted
 * <img>/<a> (rows keep identity across refreshes, so they don't remount to mint a new
 * one). Evicted/replaced URLs are retired and revoked in clearMediaCache (logout).
 */

type MediaEntry = { url: string; fingerprint: string };

const cache = new Map<string, MediaEntry>(); // insertion order = LRU recency
const retired: string[] = []; // URLs dropped from the LRU, revoked on clearMediaCache
const MEDIA_CACHE_LIMIT = 120;

// Cheap collision detector: length + head is enough to distinguish different images
// without scanning multi-MB strings.
const fingerprintOf = (base64: string): string => `${base64.length}:${base64.slice(0, 32)}`;

function base64ToBlobUrl(base64: string, mime: string): string {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

/**
 * Returns a cached object URL for the given message's media, decoding it on first use.
 * Subsequent calls for the same key + content return the same URL without re-decoding.
 */
export function getMediaObjectUrl(key: string, base64: string, mime = "image/webp"): string {
  const fingerprint = fingerprintOf(base64);
  const existing = cache.get(key);
  if (existing && existing.fingerprint === fingerprint) {
    // Refresh recency: re-insert moves this key to the most-recent end.
    cache.delete(key);
    cache.set(key, existing);
    return existing.url;
  }
  if (existing) retired.push(existing.url); // key collision — replace, revoke later

  const url = base64ToBlobUrl(base64, mime);
  cache.set(key, { url, fingerprint });

  if (cache.size > MEDIA_CACHE_LIMIT) {
    const oldestKey = cache.keys().next().value as string;
    const oldest = cache.get(oldestKey);
    if (oldest) retired.push(oldest.url);
    cache.delete(oldestKey);
  }

  return url;
}

/** Revoke and drop all cached and retired URLs (e.g. on logout). */
export function clearMediaCache(): void {
  for (const entry of cache.values()) URL.revokeObjectURL(entry.url);
  for (const url of retired) URL.revokeObjectURL(url);
  cache.clear();
  retired.length = 0;
}
